/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * CatalogComponent.tsx
 */

import {PolicyListener} from "../../services/PolicyService";
import {RouteComponentProps} from 'react-router-dom';
import Policy from "../../tools/provider/Policy";
import HiloReactComponent from "../../tools/view/layout/HiloReactComponent";
import React from "react";
import PageTitle, {TitleTab} from "../../tools/view/layout/PageTitle";
import Catalog from "../../models/Catalog";
import CatalogForm from "../../models/form/catalog/CatalogForm";
import UIToast from "../../tools/view/toast/UIToast";
import KareviHTTPService from "../../tools/service/KareviHTTPService";
import {ListWatcher} from "../../tools/list/ListAdapter";
import FormSection from "../../tools/view/form/FormSection";
import {CatalogDTO} from "../../models/dto/catalog/CatalogDTO";
import {FormListener} from "../../tools/model/form/AbstractForm";
import PageComponent from "../../tools/view/layout/PageComponent";
import FormInput, {Type} from "../../tools/view/form/FormInput";
import {TextField} from "../../tools/model/form/TextField";
import CatalogService from "../../services/CatalogService";
import NavigationService from "../../tools/service/NavigationService";
import Video from "../../models/Video";
import VideoService from "../../services/VideoService";

interface Props extends RouteComponentProps<MatchParams> {
}

interface MatchParams {
    id: string;
}

interface State {
    policy: Policy;
    pageIndex: number,
    catalog?: Catalog;
    catalogForm: CatalogForm;
    videos?: Array<Video>;
}

export default class CatalogPage extends HiloReactComponent<Props, State> implements PolicyListener, ListWatcher, FormListener<CatalogForm, CatalogDTO> {

    private readonly _catalogService: CatalogService;
    private readonly _videoService: VideoService;
    private readonly _httpService: KareviHTTPService;
    private readonly _navigationService: NavigationService;
    private _modalContainer?: Element;

    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: Props, state: State) {
        super(props, state);

        this._videoService = this._serviceRepository.resolve(VideoService.id) as VideoService;
        this._catalogService = this._serviceRepository.resolve(CatalogService.id) as CatalogService;
        this._httpService = this._serviceRepository.resolve(KareviHTTPService.id) as KareviHTTPService;
        this._navigationService = this._serviceRepository.resolve(NavigationService.id) as NavigationService;

        this.state = {
            policy: this._policyService.policy,
            pageIndex: 0,
            catalog: undefined,
            catalogForm: new CatalogForm()
        };
    }

    /**
     * onUpdate
     */
    public onUpdate(): void {
        this.forceUpdate();
    }

    /**
     * componentDidMount
     */
    public componentDidMount(): void {
        super.componentDidMount();
        this._policyService.addListener(this);

        this._modalContainer = document.querySelector('#modal-container') || undefined;
        this.state.catalogForm.addListener(this)

        this._refreshCatalog();
    }

    /**
     * _refreshCatalog
     */
    private _refreshCatalog = async () => {
        let catalog = await this._catalogService.findById(this.props.match.params.id);

        await this.state.catalogForm.load(CatalogDTO.fromCatalog(catalog));

        await this.setState({catalog: catalog});

        this._videoService.findAllInEveryCategory().then(videos => {
            console.log("find all", videos);
            let filteredVideos = videos.filter(video => this.state.catalog?.videos?.includes(video.id));
            this.setState({
                videos: filteredVideos
            })
        })
    };

    public componentWillUnmount(): void {
        this.state.catalogForm.removeListener(this);
        this._policyService.removeListener(this);
        super.componentWillUnmount();
    }

    /**
     * componentDidUpdate
     * @param prevProps
     * @param prevState
     * @param snapshot
     */
    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps.match.params.id !== this.props.match.params.id) {
            this._refreshCatalog();
        }
    }


    /**
     * policyUpdated
     * @param policy
     */
    public policyUpdated = (policy: Policy) => {
        this.setState({
            policy: policy
        })
    };

    /**
     * _catalogView
     * @returns {any}
     * @private
     */
    private _catalogView = (props: { form: CatalogForm, catalog?: Catalog }) => {
        let catalogIdField = new TextField("catalog_id", t => t.catalogId, _ => true, Type.TEXT, false, props.catalog?.id, "", true)

        return <>
            <FormInput field={catalogIdField}/>
            <FormSection label={this.t.information} form={props.form} submit={this._saveCatalog}/>
            <p>{this.t.videoList}</p>
            <ul>
                {
                    this.state.videos && this.state.videos.length > 0 ?
                        this.state.videos
                            .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()))
                            .map(video => <li key={video.id}><a href={`/category/${video.categoryId}/video/${video.id}`}>{video.title}</a></li>)
                        :
                        <p>{this.t.videoListEmpty}</p>
                }
            </ul>
        </>
    };

    /**
     * _saveCatalog
     * @param dto
     * @private
     */
    private _saveCatalog = async (dto: Map<string, any> | FormData) => {
        const {catalog} = this.state;
        if (!catalog) return console.log("catalog is undefined.");

        try {
            await this._catalogService.updateCatalog(catalog, dto);
            this.state.catalogForm.statusCode = 200;
            UIToast.default.show(this.t.catalogEdited)
            await this._refreshCatalog();
        } catch (e) {
            this.state.catalogForm.statusCode = e.status
            console.error(e);
        }
    };

    /**
     * onFormUpdate
     * @param form
     */
    public onFormUpdate = (form: CatalogForm) => {
        this.setState({catalogForm: form})
    };

    /**
     * render
     */
    public render(): any {
        const {catalog, catalogForm} = this.state;
        const titleTabs: TitleTab[] = [
            {
                title: t => t.details, content: <>
                    <this._catalogView form={catalogForm} catalog={catalog}/>
                </>
            },
        ];

        return <PageComponent>
            <PageTitle title={t => t.catalogs}
                       tabs={titleTabs}
                       onTabSelected={index => this.setState({pageIndex: index})}
                       subTitle={catalog?.title || this.t.loading}
                       backLabel={t => t.allCatalogs}
            />
            {titleTabs[this.state.pageIndex].content}
        </PageComponent>
    }
}
